import React, {useState, useEffect} from 'react';
import {Select, Spin, Avatar} from 'antd';
import {UserOutlined} from '@ant-design/icons';
import {useTranslation} from '../../hooks/useTranslation';
import {useUser} from "./hooks/useUser";
import {User} from "../../types";

interface SelectUserProps {
  value?: string;
  placeholder?: string;
  onChange?: (value: string, option: User) => void;
  disabled?: boolean;
  allowClear?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: React.CSSProperties;
  className?: string;
  mode?: 'multiple' | 'tags';
  id?: string;
}

const SelectUser: React.FC<SelectUserProps> = ({
                                                 value,
                                                 placeholder,
                                                 onChange,
                                                 disabled = false,
                                                 allowClear = true,
                                                 size = 'middle',
                                                 style,
                                                 className,
                                                 mode,
                                                 id
                                               }) => {
  const {t} = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');

  // Debounce search input to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Determine if we should fetch data
  const shouldFetch = isDropdownOpen && (debouncedSearchValue.length === 0 || debouncedSearchValue.length > 2);

  // Use the useUser hook to fetch data - only when conditions are met
  const {data: users = [], isLoading, error} = useUser(
    {
      valueFind: debouncedSearchValue
    },
    {
      enabled: shouldFetch
    }
  );

  // Helper function to get the user display name
  const getUserDisplayName = (user: User): string => {

    if (user.fullName) return user.fullName;
    if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
    if (user.username) return user.username;
    return user.description || user.email || 'Unknown User';
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    setIsDropdownOpen(open);
  };

  const handleChange = (selectedValue: string | undefined) => {
    if (selectedValue === undefined || selectedValue === null) {
      // Handle clear action
      if (onChange) {
        onChange('', {} as User);
      }
      return;
    }

    // Find the selected user directly from the users array
    const selectedUser = users.find(user => getUserDisplayName(user) === selectedValue);
    if (onChange && selectedUser) {
      onChange(selectedValue, selectedUser);
    }
  };

  const handleSearch = (searchText: string) => {
    console.log('Search text changed:', searchText); // Debug log
    setSearchValue(searchText);
  };

  // Helper function to get user initials
  const getUserInitials = (user: User): string => {
    if (user.firstName && user.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    if (user.fullName) {
      const names = user.fullName.split(' ');
      if (names.length >= 2) {
        return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`.toUpperCase();
      }
      return user.fullName.charAt(0).toUpperCase();
    }
    if (user.username) {
      return user.username.charAt(0).toUpperCase();
    }
    return 'U';
  };

  if (error) {
    console.error('Error loading users:', error);
  }

  return (
    <Select
      id={id}
      value={value}
      placeholder={placeholder || t('messages.select')}
      onChange={handleChange}
      onSearch={handleSearch}
      onOpenChange={handleDropdownVisibleChange}
      onSelect={(value, option) => {}}
      disabled={disabled}
      allowClear={allowClear}
      size={size}
      style={style}
      className={className}
      mode={mode}
      showSearch
      loading={isLoading}
      notFoundContent={isLoading ? <Spin size="small"/> : t('messages.notFound')}
      filterOption={false} // Disable client-side filtering since we're doing server-side search
      optionLabelProp="label" // This makes the selected value show only the label, not the full option content
    >
      {users.map((user) => {
        const displayName = getUserDisplayName(user);
        const key = user.id || user.username || Math.random().toString();

        return (
          <Select.Option
            key={key}
            value={displayName}
            label={displayName} // Simple label for selected display
            title={displayName}
          >
            {/* Full detailed content only shown in dropdown */}
            <div style={{display: 'flex', alignItems: 'center'}}>
              <Avatar
                size={24}
                src={user.avatar}
                icon={<UserOutlined />}
                style={{
                  marginRight: 8,
                  backgroundColor: user.avatar ? 'transparent' : '#1890ff',
                  flexShrink: 0
                }}
              >
                {!user.avatar && getUserInitials(user)}
              </Avatar>
              <div style={{display: 'flex', flexDirection: 'column', minWidth: 0}}>
                <span style={{
                  fontSize: '14px',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {displayName}
                </span>
                {user.email && (
                  <span style={{
                    fontSize: '12px',
                    color: '#8c8c8c',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {user.email}
                  </span>
                )}
                {user.role && (
                  <span style={{
                    fontSize: '11px',
                    color: '#bfbfbf',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {user.role}
                  </span>
                )}
              </div>
            </div>
          </Select.Option>
        );
      })}
    </Select>
  );
};

export default SelectUser;
